// Best-effort punctuation for dictated text. The Web Speech API returns
// bare words with no terminal marks or capitalization — this is a small
// heuristic layer, not real NLP, so treat it as "usually right for short
// remarks," not "grammatically correct." Only applied to speech-finalized
// chunks (see `gable-dictation.controller.ts`); anything typed is left exactly as typed.
//
// Two things it does:
// - Capitalizes the chunk and ends it with `.` or `?`, guessing `?` from a
//   leading question word (who/what/is/are/do/…).
// - Web Speech finalizes on a pause, which is usually one sentence — but
//   short remarks spoken without a real pause ("hello how are you") land in
//   a single final. A wh-word appearing after the first word is a fairly
//   unambiguous signal of a second sentence starting, so it splits there.
//   Helper verbs (is/are/do/…) are deliberately NOT split triggers — they're
//   too common mid-sentence ("my name is Dima") to use as a boundary.

const SENTENCE_BREAK_WORDS = new Set([
  'who', 'whos', 'whom', 'whose', 'what', 'whats', 'when', 'whens',
  'where', 'wheres', 'why', 'whys', 'how', 'hows', 'which',
]);

const QUESTION_LEAD_WORDS = new Set([
  ...SENTENCE_BREAK_WORDS,
  'is', 'isnt', 'are', 'arent', 'was', 'wasnt', 'were', 'werent', 'am',
  'do', 'dont', 'does', 'doesnt', 'did', 'didnt',
  'can', 'cant', 'could', 'couldnt',
  'will', 'wont', 'would', 'wouldnt',
  'shall', 'should', 'shouldnt', 'may', 'might',
  'have', 'havent', 'has', 'hasnt', 'had', 'hadnt',
]);

const bareWord = (word: string) => word.toLowerCase().replace(/[^a-z]/g, '');

const capitalize = (text: string) =>
  text.length ? text[0].toUpperCase() + text.slice(1) : text;

const isQuestion = (sentence: string) => {
  const first = sentence.trim().split(/\s+/)[0];
  return first ? QUESTION_LEAD_WORDS.has(bareWord(first)) : false;
};

const punctuateSentence = (sentence: string) => {
  const trimmed = sentence.trim();
  if (!trimmed) return trimmed;
  // already ended by the recognizer or a stray typed mark — don't double up
  if (/[.?!]$/.test(trimmed)) return capitalize(trimmed);
  return `${capitalize(trimmed)}${isQuestion(trimmed) ? '?' : '.'}`;
};

/** Punctuates one finalized speech-recognition chunk. Empty input passes through. */
export function punctuateChunk(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return trimmed;

  const words = trimmed.split(/\s+/);
  const breakIndex = words.findIndex(
    (word, i) => i > 0 && SENTENCE_BREAK_WORDS.has(bareWord(word)),
  );

  if (breakIndex > 0 && breakIndex < words.length - 1) {
    const first = punctuateSentence(words.slice(0, breakIndex).join(' '));
    const second = punctuateSentence(words.slice(breakIndex).join(' '));
    return `${first} ${second}`;
  }

  return punctuateSentence(trimmed);
}
